import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  Button,
  HStack,
  VStack,
  IconButton,
  Input,
  NumberInput,
  NumberInputField,
  Text,
  Badge,
  Collapse,
  useDisclosure,
  Tooltip,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Flex,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import {
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineExclamationCircle,
  HiOutlineViewGrid,
  HiOutlineArrowRight,
} from "react-icons/hi";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import CodesetSelect from "../CodesetSelect";
import CodesetSelectWithFilters from "../CodesetSelectWithFilters";

/**
 * Cell renderer for code selection with CodesetSelect and custom input
 */
const CodeCellRenderer = (props) => {
  const { value, data, context } = props;
  const { parentAgencyCodeSet, onChange } = context;
  const effectiveCodeset = data.agencyCodeSet || parentAgencyCodeSet || "";

  const handleChange = (newCode) => {
    onChange(data.rowIndex, { ...data, code: newCode });
  };

  return (
    <HStack spacing={1} height="100%" py={1}>
      {effectiveCodeset && (
        <CodesetSelect
          codesetCode={effectiveCodeset}
          value={value ? { code: value, description: "" } : null}
          onChange={(val) => handleChange(val?.code || "")}
          size="xs"
          flex="1"
        />
      )}
      <Input
        size="xs"
        placeholder={effectiveCodeset ? "or custom" : "Code"}
        value={value || ""}
        onChange={(e) => handleChange(e.target.value)}
        flex="1"
        minW="80px"
      />
    </HStack>
  );
};

/**
 * Cell renderer for allocation percent
 */
const AllocationPercentCellRenderer = (props) => {
  const { value, data, context } = props;
  const { nodeAmount, onChange } = context;

  const handleChange = (e) => {
    const inputValue = e.target.value;
    const numValue = parseFloat(inputValue);
    const validPercent = isNaN(numValue) ? 0 : numValue;
    const newAmount = (nodeAmount * validPercent) / 100;
    onChange(data.rowIndex, {
      ...data,
      allocationPercent: validPercent,
      allocationAmount: newAmount,
    });
  };

  return (
    <Input
      size="xs"
      type="number"
      min={0}
      max={100}
      step={0.01}
      value={value ?? 0}
      onChange={handleChange}
      textAlign="right"
      p={1}
    />
  );
};

/**
 * Cell renderer for actions (split and delete)
 */
const ActionsCellRenderer = (props) => {
  const { data, context } = props;
  const { onDelete, onSplitToChild } = context;

  return (
    <HStack spacing={0} height="100%" alignItems="center">
      {onSplitToChild && (
        <Tooltip label="Split to child node">
          <IconButton
            aria-label="Split to child"
            icon={<HiOutlineViewGrid />}
            size="xs"
            variant="ghost"
            colorScheme="blue"
            onClick={() => onSplitToChild(data.rowIndex)}
          />
        </Tooltip>
      )}
      <IconButton
        aria-label="Delete value"
        icon={<HiOutlineTrash />}
        size="xs"
        variant="ghost"
        onClick={() => onDelete(data.rowIndex)}
      />
    </HStack>
  );
};

/**
 * Factor Values Grid Component using agGrid
 */
const FactorValuesGrid = ({
  factorValues,
  nodeAmount,
  parentAgencyCodeSet,
  onUpdateValue,
  onDeleteValue,
  onSplitValueToChild,
  onAddValue,
  onSplitAllValues,
}) => {
  // Format currency
  const currency = (n) =>
    typeof n === "number"
      ? n.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "";

  // Add row index to each factor value for tracking
  const rowData = useMemo(
    () =>
      factorValues.map((fv, index) => ({
        ...fv,
        rowIndex: index,
        allocationAmount: (nodeAmount * (fv.allocationPercent || 0)) / 100,
      })),
    [factorValues, nodeAmount]
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Code",
        field: "code",
        cellRenderer: CodeCellRenderer,
        flex: 2,
        minWidth: 200,
      },
      {
        headerName: "Alloc%",
        field: "allocationPercent",
        cellRenderer: AllocationPercentCellRenderer,
        width: 100,
        cellClass: "ag-right-aligned-cell",
      },
      {
        headerName: "Amount",
        field: "allocationAmount",
        valueFormatter: (params) => currency(params.value),
        width: 120,
        cellClass: "ag-right-aligned-cell",
      },
      {
        headerName: "Actions",
        cellRenderer: ActionsCellRenderer,
        width: 100,
        sortable: false,
        filter: false,
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      resizable: true,
      filter: false,
    }),
    []
  );

  const context = useMemo(
    () => ({
      parentAgencyCodeSet,
      nodeAmount,
      onChange: onUpdateValue,
      onDelete: onDeleteValue,
      onSplitToChild: onSplitValueToChild,
    }),
    [
      parentAgencyCodeSet,
      nodeAmount,
      onUpdateValue,
      onDeleteValue,
      onSplitValueToChild,
    ]
  );

  return (
    <Box borderWidth="1px" borderRadius="md" p={2} bg="gray.50">
      <HStack justify="space-between" mb={2}>
        <Text
          fontSize="xs"
          fontWeight="bold"
          color="gray.600"
          textTransform="uppercase">
          Factor Values {factorValues?.length ? `(${factorValues.length})` : ""}
        </Text>
        <HStack spacing={2}>
          {factorValues?.length > 0 && (
            <Button
              size="xs"
              variant="outline"
              leftIcon={<HiOutlineViewGrid />}
              onClick={onSplitAllValues}
              colorScheme="blue">
              Split All to Children
            </Button>
          )}
          <Button
            size="xs"
            variant="solid"
            leftIcon={<HiOutlinePlus />}
            onClick={onAddValue}
            colorScheme="blue">
            Add Value
          </Button>
        </HStack>
      </HStack>

      {factorValues.length > 0 ? (
        <Box
          className="ag-theme-alpine"
          height={`${Math.min(factorValues.length * 35 + 40, 300)}px`}
          bg="white"
          borderRadius="md">
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            context={context}
            rowHeight={35}
            headerHeight={35}
            suppressMovableColumns={true}
            suppressCellFocus={false}
            theme="legacy"
          />
        </Box>
      ) : (
        <Text
          fontSize="xs"
          color="gray.500"
          fontStyle="italic"
          p={2}
          textAlign="center">
          No factor values. Add values to define allocations, then split to
          child nodes if needed.
        </Text>
      )}
    </Box>
  );
};

/**
 * Recursively renders a single allocation tree node - COMPACT VERSION
 */
const AllocationTreeNode = ({
  node,
  level = 0,
  onUpdateNode,
  onAddChild,
  onDeleteNode,
  onSplitValueToChild,
  premium = 0,
  parentAmount = 0,
  onCopyFactors = null,
  enableFactorCopy = false,
}) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: level < 2 });
  const [isEditingName, setIsEditingName] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const cancelDeleteRef = useRef();

  // Track whether the current factor type uses codes from a codeset
  // If false, agency code and codeset fields should be hidden
  const [showCodesetFields, setShowCodesetFields] = useState(true);

  // Check if current factorType has #NoCodes filter on mount and when factorType changes
  useEffect(() => {
    const checkFactorTypeFilter = async () => {
      if (!node.factorType) {
        setShowCodesetFields(true);
        return;
      }

      try {
        // Fetch the PANNER001 codeset to check the filter for this factorType
        const response = await fetch(
          `https://codify.poc.mrc.ai/api/codesets/PANNER001`
        );
        if (!response.ok) {
          console.error("Failed to fetch codeset");
          return;
        }

        const codeset = await response.json();
        const factorTypeCode = codeset.codes?.find(
          (c) => c.code === node.factorType
        );

        if (factorTypeCode?.filter) {
          const hasNoCodes = factorTypeCode.filter.includes("#NoCodes");
          setShowCodesetFields(!hasNoCodes);
        } else {
          setShowCodesetFields(true);
        }
      } catch (error) {
        console.error("Error checking factor type filter:", error);
      }
    };

    checkFactorTypeFilter();
  }, [node.factorType]);

  const hasChildren = node.children && node.children.length > 0;
  const isRoot = level === 0;

  // Calculate allocation amount based on parent
  const nodeAmount = isRoot
    ? premium
    : (parentAmount * (node.allocationPercent || 0)) / 100;

  // Validate children allocations sum to 100%
  const childrenSum = hasChildren
    ? node.children.reduce((sum, c) => sum + (c.allocationPercent || 0), 0)
    : 0;
  const hasAllocationError = hasChildren && Math.abs(childrenSum - 100) > 0.01;

  // Validate factor values sum to 100%
  const valuesSum = (node.factorValues || []).reduce(
    (sum, v) => sum + (v.allocationPercent || 0),
    0
  );
  const hasValuesError =
    node.factorValues?.length > 0 && Math.abs(valuesSum - 100) > 0.01;

  const handleUpdate = (patch) => {
    onUpdateNode(node.id, { ...node, ...patch });
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    setIsDeleteDialogOpen(false);
    onDeleteNode(node.id);
  };

  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleCopyFactors = () => {
    if (onCopyFactors) {
      // Extract factor configuration from this node
      const factorConfig = {
        level: node.level,
        factorType: node.factorType,
        agencyCode: node.agencyCode,
        agencyCodeSet: node.agencyCodeSet,
        factorValues: node.factorValues || [],
      };
      onCopyFactors(factorConfig, level);
    }
  };

  const handleAddChild = () => {
    const existingChildren = node.children || [];
    const childrenCount = existingChildren.length;

    // Auto-distribute allocation
    let newAllocationPercent = 100;
    const updatedChildren = [...existingChildren];

    if (childrenCount === 0) {
      // First child gets 100%
      newAllocationPercent = 100;
    } else {
      // Check if any child has been manually edited (not evenly distributed)
      const evenDistribution = 100 / childrenCount;
      const allChildrenEven = existingChildren.every(
        (child) =>
          Math.abs((child.allocationPercent || 0) - evenDistribution) < 0.01
      );

      if (allChildrenEven) {
        // Redistribute evenly among all children including the new one
        const newEvenDistribution = 100 / (childrenCount + 1);
        updatedChildren.forEach((child) => {
          child.allocationPercent = newEvenDistribution;
        });
        newAllocationPercent = newEvenDistribution;
      } else {
        // Children have been manually edited, add new child with 0%
        newAllocationPercent = 0;
      }
    }

    const newChild = {
      id: crypto.randomUUID(),
      name: "New Node",
      level: level + 1,
      allocationPercent: newAllocationPercent,
      allocationAmount: (nodeAmount * newAllocationPercent) / 100,
      factorType: "",
      agencyCode: "",
      agencyCodeSet: "",
      factorValues: [
        {
          id: crypto.randomUUID(),
          code: "",
          description: "",
          agencyCode: "",
          agencyCodeSet: "",
          allocationPercent: 100,
          allocationAmount: nodeAmount,
          isCustom: false,
        },
      ],
      children: [],
    };

    // Update existing children if we redistributed
    if (updatedChildren.length > 0 && updatedChildren !== existingChildren) {
      handleUpdate({ children: [...updatedChildren, newChild] });
    } else {
      onAddChild(node.id, newChild);
    }

    if (!isOpen) onToggle();
  };

  const addFactorValue = () => {
    const values = node.factorValues || [];
    const valuesCount = values.length;

    // Auto-distribute allocation
    let newAllocationPercent = 100;
    const updatedValues = [...values];

    if (valuesCount === 0) {
      // First value gets 100%
      newAllocationPercent = 100;
    } else {
      // Check if values are evenly distributed
      const evenDistribution = 100 / valuesCount;
      const allValuesEven = values.every(
        (v) => Math.abs((v.allocationPercent || 0) - evenDistribution) < 0.01
      );

      if (allValuesEven) {
        // Redistribute evenly among all values including the new one
        const newEvenDistribution = 100 / (valuesCount + 1);
        updatedValues.forEach((v) => {
          v.allocationPercent = newEvenDistribution;
          v.allocationAmount = (nodeAmount * newEvenDistribution) / 100;
        });
        newAllocationPercent = newEvenDistribution;
      } else {
        // Values have been manually edited, add new value with 0%
        newAllocationPercent = 0;
      }
    }

    const newAllocationAmount = (nodeAmount * newAllocationPercent) / 100;

    handleUpdate({
      factorValues: [
        ...updatedValues,
        {
          id: crypto.randomUUID(),
          code: "",
          description: "",
          agencyCode: node.agencyCode || "",
          agencyCodeSet: node.agencyCodeSet || "",
          allocationPercent: newAllocationPercent,
          allocationAmount: newAllocationAmount,
          isCustom: false,
        },
      ],
    });
  };

  const updateFactorValue = (idx, patch) => {
    const values = [...(node.factorValues || [])];
    // Remove rowIndex and recalculated allocationAmount fields that were added for agGrid
    const { rowIndex, ...cleanPatch } = patch;
    values[idx] = cleanPatch;
    handleUpdate({ factorValues: values });
  };

  const deleteFactorValue = (idx) => {
    const values = [...(node.factorValues || [])];
    values.splice(idx, 1);
    handleUpdate({ factorValues: values });
  };

  const handleSplitValueToChild = (valueIdx) => {
    const value = node.factorValues[valueIdx];
    if (!value) return;

    // Create a new sibling node from this value
    const newSibling = {
      id: crypto.randomUUID(),
      name: value.code || "Unnamed",
      level: level,
      allocationPercent: value.allocationPercent || 0,
      allocationAmount: (parentAmount * (value.allocationPercent || 0)) / 100,
      factorType: node.factorType || "",
      agencyCode: value.agencyCode || node.agencyCode || "",
      agencyCodeSet: value.agencyCodeSet || node.agencyCodeSet || "",
      factorValues: [],
      children: [],
    };

    onSplitValueToChild(node.id, valueIdx, newSibling);
  };

  const handleSplitAllValues = () => {
    if (!node.factorValues || node.factorValues.length === 0) return;

    const newSiblings = node.factorValues.map((value) => {
      const siblingAmount =
        (parentAmount * (value.allocationPercent || 0)) / 100;

      // Create a child node for this sibling with first factor value
      const childNode = {
        id: crypto.randomUUID(),
        name: "New Node",
        level: level + 1,
        allocationPercent: 100,
        allocationAmount: siblingAmount,
        factorType: "",
        agencyCode: "",
        agencyCodeSet: "",
        factorValues: [
          {
            id: crypto.randomUUID(),
            code: "",
            description: "",
            agencyCode: "",
            agencyCodeSet: "",
            allocationPercent: 100,
            allocationAmount: siblingAmount,
            isCustom: false,
          },
        ],
        children: [],
      };

      return {
        id: crypto.randomUUID(),
        name: value.code || "Unnamed",
        level: level,
        allocationPercent: value.allocationPercent || 0,
        allocationAmount: siblingAmount,
        factorType: node.factorType || "",
        agencyCode: value.agencyCode || node.agencyCode || "",
        agencyCodeSet: value.agencyCodeSet || node.agencyCodeSet || "",
        factorValues: [],
        children: [childNode], // Add the child node
      };
    });

    // Replace this node with all the new siblings
    onSplitValueToChild(node.id, -1, newSiblings);
  };

  return (
    <Box position="relative">
      {/* Main Node Box */}
      <Box
        borderWidth="2px"
        borderRadius="lg"
        bg={isRoot ? "blue.50" : "white"}
        borderColor={
          hasAllocationError || hasValuesError
            ? "red.400"
            : isRoot
            ? "blue.300"
            : "gray.300"
        }
        mb={3}
        overflow="hidden"
        fontSize="sm"
        boxShadow={isRoot ? "lg" : "md"}
        transition="all 0.2s"
        _hover={{ boxShadow: "xl" }}
        position="relative">
        {/* Node Header - More prominent */}
        <HStack
          spacing={2}
          px={3}
          py={2}
          bg={isRoot ? "blue.100" : "gray.100"}
          borderBottomWidth="2px"
          borderBottomColor={isRoot ? "blue.200" : "gray.200"}>
          {/* Expand/Collapse */}
          {hasChildren ? (
            <IconButton
              aria-label="Toggle"
              icon={
                isOpen ? <HiOutlineChevronDown /> : <HiOutlineChevronRight />
              }
              size="sm"
              variant="ghost"
              onClick={onToggle}
            />
          ) : (
            <Box w="32px" />
          )}

          {/* Level Badge */}
          {!isRoot && (
            <Badge
              colorScheme="blue"
              fontSize="xs"
              px={2}
              py={1}
              borderRadius="md">
              {level}
            </Badge>
          )}

          {/* Node Name - Larger and more prominent */}
          {isEditingName ? (
            <Input
              size="sm"
              value={node.name || ""}
              onChange={(e) => handleUpdate({ name: e.target.value })}
              onBlur={() => setIsEditingName(false)}
              autoFocus
              flex="1"
              fontWeight="semibold"
            />
          ) : (
            <Text
              fontSize="sm"
              fontWeight={isRoot ? "bold" : "semibold"}
              cursor="pointer"
              onClick={() => !isRoot && setIsEditingName(true)}
              flex="1"
              noOfLines={1}
              color={isRoot ? "blue.800" : "gray.700"}>
              {node.name || "(unnamed)"}
            </Text>
          )}

          {/* Error Indicators */}
          {hasAllocationError && (
            <Tooltip
              label={`Children sum to ${childrenSum.toFixed(1)}% (need 100%)`}>
              <Badge colorScheme="red" fontSize="xs">
                <HStack spacing={1}>
                  <HiOutlineExclamationCircle />
                  <Text>Children: {childrenSum.toFixed(1)}%</Text>
                </HStack>
              </Badge>
            </Tooltip>
          )}
          {hasValuesError && (
            <Tooltip
              label={`Values sum to ${valuesSum.toFixed(1)}% (need 100%)`}>
              <Badge colorScheme="orange" fontSize="xs">
                <HStack spacing={1}>
                  <HiOutlineExclamationCircle />
                  <Text>Values: {valuesSum.toFixed(1)}%</Text>
                </HStack>
              </Badge>
            </Tooltip>
          )}

          {/* Allocation Display - More prominent */}
          {!isRoot && (
            <Box>
              {/* Action buttons - top right corner, inside the box */}
              <HStack
                spacing={1}
                position="absolute"
                top={2}
                right={2}
                zIndex={10}>
                <Badge
                  colorScheme="green"
                  fontSize="sm"
                  px={3}
                  py={1}
                  borderRadius="md"
                  mr={2}>
                  {nodeAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Badge>
                <Button
                  aria-label="Add child node"
                  leftIcon={<HiOutlinePlus />}
                  size="xs"
                  colorScheme="blue"
                  onClick={handleAddChild}>
                  Add
                </Button>
                <Button
                  aria-label="Delete node"
                  leftIcon={<HiOutlineTrash />}
                  size="xs"
                  colorScheme="gray"
                  onClick={handleDeleteClick}>
                  Delete
                </Button>

                {/* Copy Factors Button */}
                {enableFactorCopy && !isRoot && (
                  <Tooltip
                    label={`Copy factor configuration from this level (${level}) to accounting tree`}>
                    <Button
                      aria-label="Copy factors"
                      leftIcon={<HiOutlineArrowRight />}
                      size="sm"
                      variant="outline"
                      colorScheme="whatsapp"
                      onClick={handleCopyFactors}>
                      Copy
                    </Button>
                  </Tooltip>
                )}
              </HStack>
            </Box>
          )}

          {/* Root total */}
          {isRoot && (
            <Box>
              <Badge
                colorScheme="blue"
                fontSize="md"
                px={3}
                py={1}
                borderRadius="md"
                mr={16}>
                Total:{" "}
                {premium.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Badge>
              <Button
                aria-label="Add child node"
                leftIcon={<HiOutlinePlus />}
                size="xs"
                colorScheme="blue"
                position="absolute"
                top={2}
                right={2}
                zIndex={10}
                onClick={handleAddChild}>
                Add
              </Button>
            </Box>
          )}
        </HStack>

        {/* Node Body */}
        {!isRoot && (
          <Box p={3} bg="white">
            {/* Factor Definition - Clearer layout */}
            <VStack spacing={2} align="stretch" mb={3}>
              <Flex gap={2} flexWrap="wrap">
                <FormControl flex="0 0 80px">
                  <FormLabel
                    fontSize="xs"
                    mb={1}
                    fontWeight="semibold"
                    alignContent="right">
                    Alloc%
                  </FormLabel>
                  <NumberInput
                    size="sm"
                    min={0}
                    max={100}
                    precision={2}
                    value={node.allocationPercent || 0}
                    onChange={(_, n) => handleUpdate({ allocationPercent: n })}>
                    <NumberInputField />
                  </NumberInput>
                </FormControl>

                <FormControl flex="1" minW="140px">
                  <FormLabel fontSize="xs" mb={1} fontWeight="semibold">
                    Factor Type
                  </FormLabel>
                  <CodesetSelectWithFilters
                    codesetCode="PANNER001"
                    size="sm"
                    placeholder="Select type..."
                    value={
                      node.factorType && node.factorType !== ""
                        ? { code: node.factorType, description: "" }
                        : null
                    }
                    onChange={(selected) => {
                      const updates = {
                        factorType: selected?.code || "",
                      };

                      // Check if this factor type uses codes from a codeset
                      // If filter contains '#NoCodes', agency and codeset don't apply
                      const hasNoCodes =
                        selected?.filter &&
                        selected.filter.includes("#NoCodes");

                      // Update UI state to show/hide codeset fields
                      setShowCodesetFields(!hasNoCodes);

                      if (hasNoCodes) {
                        // Clear agency code and codeset since codes don't apply
                        updates.agencyCode = "";
                        updates.agencyCodeSet = "";

                        // Also clear agency code and codeset from all factor values
                        const updatedValues = (node.factorValues || []).map(
                          (v) => ({
                            ...v,
                            agencyCode: "",
                            agencyCodeSet: "",
                          })
                        );
                        updates.factorValues = updatedValues;
                      } else {
                        // If the selected factor type has agency and codelist values, copy them
                        if (selected?.codeListAgency) {
                          updates.agencyCode = selected.codeListAgency;

                          // Also update all factor values with the new agency code
                          const updatedValues = (node.factorValues || []).map(
                            (v) => ({
                              ...v,
                              agencyCode: selected.codeListAgency,
                            })
                          );
                          updates.factorValues = updatedValues;
                        }

                        if (selected?.codeListSource) {
                          updates.agencyCodeSet = selected.codeListSource;

                          // Also update all factor values with the new codeset
                          const updatedValues =
                            updates.factorValues ||
                            (node.factorValues || []).map((v) => ({
                              ...v,
                              agencyCodeSet: selected.codeListSource,
                              // Clear code if codeset changed (unless it's a custom code)
                              code: v.isCustom ? v.code : "",
                            }));
                          updates.factorValues = updatedValues;
                        }
                      }

                      handleUpdate(updates);
                    }}
                    filters={{ isFactorType: true }}
                  />
                </FormControl>

                {showCodesetFields && (
                  <>
                    <FormControl flex="1" minW="120px">
                      <FormLabel fontSize="xs" mb={1} fontWeight="semibold">
                        Agency Code
                      </FormLabel>
                      <CodesetSelect
                        codesetCode="3055"
                        filter="#Panner"
                        size="sm"
                        placeholder="Select agency..."
                        value={
                          node.agencyCode && node.agencyCode !== ""
                            ? { code: node.agencyCode, description: "" }
                            : null
                        }
                        onChange={(selected) => {
                          const agency = selected?.code || "";
                          const updatedValues = (node.factorValues || []).map(
                            (v) => ({
                              ...v,
                              agencyCode: agency,
                            })
                          );
                          handleUpdate({
                            agencyCode: agency,
                            factorValues: updatedValues,
                          });
                        }}
                      />
                    </FormControl>

                    <FormControl flex="1" minW="120px">
                      <FormLabel fontSize="xs" mb={1} fontWeight="semibold">
                        Agency Codeset
                      </FormLabel>
                      <CodesetSelect
                        codesetCode="C01"
                        size="sm"
                        placeholder="Select codeset..."
                        value={
                          node.agencyCodeSet && node.agencyCodeSet !== ""
                            ? { code: node.agencyCodeSet, description: "" }
                            : null
                        }
                        onChange={(selected) => {
                          const codeset = selected?.code || "";
                          const updatedValues = (node.factorValues || []).map(
                            (v) => ({
                              ...v,
                              agencyCodeSet: codeset,
                              code: v.isCustom ? v.code : "",
                            })
                          );
                          handleUpdate({
                            agencyCodeSet: codeset,
                            factorValues: updatedValues,
                          });
                        }}
                      />
                    </FormControl>
                  </>
                )}
              </Flex>
            </VStack>

            {/* Factor Values - Only show if node has no children */}
            {!hasChildren && (
              <FactorValuesGrid
                factorValues={node.factorValues || []}
                nodeAmount={nodeAmount}
                parentAgencyCodeSet={node.agencyCodeSet}
                onUpdateValue={updateFactorValue}
                onDeleteValue={deleteFactorValue}
                onSplitValueToChild={handleSplitValueToChild}
                onAddValue={addFactorValue}
                onSplitAllValues={handleSplitAllValues}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Children - with visual connector */}
      <Collapse in={isOpen} animateOpacity>
        {hasChildren && (
          <Box
            position="relative"
            pl="40px"
            _before={{
              content: '""',
              position: "absolute",
              left: "20px",
              top: "0",
              bottom: "0",
              width: "2px",
              bg: "gray.300",
            }}>
            {/* Children container - vertical stack */}
            <VStack align="stretch" spacing={3} position="relative">
              {node.children.map((child, idx) => (
                <Box
                  key={child.id}
                  position="relative"
                  _before={{
                    content: '""',
                    position: "absolute",
                    left: "-20px",
                    top: "50%",
                    width: "20px",
                    height: "2px",
                    bg: "gray.300",
                  }}
                  _after={
                    idx === node.children.length - 1
                      ? {
                          content: '""',
                          position: "absolute",
                          left: "-20px",
                          top: "50%",
                          bottom: "-100%",
                          width: "2px",
                          bg: "white",
                        }
                      : {}
                  }>
                  <AllocationTreeNode
                    node={child}
                    level={level + 1}
                    onUpdateNode={onUpdateNode}
                    onAddChild={onAddChild}
                    onDeleteNode={onDeleteNode}
                    onSplitValueToChild={onSplitValueToChild}
                    premium={premium}
                    parentAmount={nodeAmount}
                    onCopyFactors={onCopyFactors}
                    enableFactorCopy={enableFactorCopy}
                  />
                </Box>
              ))}
            </VStack>
          </Box>
        )}
      </Collapse>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={cancelDelete}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Node
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete the node "{node.name || "Unnamed"}
              "?
              {hasChildren && (
                <Text
                  mt={2}
                  fontSize="sm"
                  color="orange.600"
                  fontWeight="semibold">
                  Warning: This will also delete all {node.children.length}{" "}
                  child node(s).
                </Text>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelDeleteRef} onClick={cancelDelete}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

/**
 * Main component for editing the hierarchical allocation tree
 */
const AllocationTreeEditor = ({
  tree,
  onChange,
  factors = [],
  premium = 0,
  title = "Placing Allocation Tree",
  onCopyFactors = null, // Optional callback to enable factor copying
  enableFactorCopy = false, // Whether to show copy buttons
}) => {
  const [validationErrors, setValidationErrors] = useState([]);

  const updateNodeById = (root, nodeId, updates) => {
    if (root.id === nodeId) {
      return { ...root, ...updates };
    }
    if (root.children) {
      return {
        ...root,
        children: root.children.map((child) =>
          updateNodeById(child, nodeId, updates)
        ),
      };
    }
    return root;
  };

  const addChildById = (root, parentId, newChild) => {
    if (root.id === parentId) {
      const children = root.children || [];
      return {
        ...root,
        children: [...children, newChild],
      };
    }
    if (root.children) {
      return {
        ...root,
        children: root.children.map((child) =>
          addChildById(child, parentId, newChild)
        ),
      };
    }
    return root;
  };

  const deleteNodeById = (root, nodeId) => {
    if (root.children) {
      return {
        ...root,
        children: root.children
          .filter((child) => child.id !== nodeId)
          .map((child) => deleteNodeById(child, nodeId)),
      };
    }
    return root;
  };

  const splitValueToChild = (root, nodeId, valueIdx, newSiblings) => {
    // Special case: splitting all values (valueIdx = -1)
    if (valueIdx === -1 && root.id === nodeId) {
      // This shouldn't happen at root level
      return root;
    }

    if (root.children) {
      const newChildren = [];
      let replaced = false;

      root.children.forEach((child) => {
        if (child.id === nodeId) {
          replaced = true;
          if (valueIdx === -1) {
            // Split all: replace this node with multiple siblings
            if (Array.isArray(newSiblings)) {
              newChildren.push(...newSiblings);
            }
          } else {
            // Split one: keep the node but remove the value, add sibling
            const values = [...(child.factorValues || [])];
            values.splice(valueIdx, 1);
            newChildren.push({
              ...child,
              factorValues: values,
            });
            if (!Array.isArray(newSiblings)) {
              newChildren.push(newSiblings);
            }
          }
        } else {
          // Recursively search in children
          const updated = splitValueToChild(
            child,
            nodeId,
            valueIdx,
            newSiblings
          );
          newChildren.push(updated);
        }
      });

      return {
        ...root,
        children: newChildren,
      };
    }
    return root;
  };

  const validateTree = (node, errors = []) => {
    if (node.children && node.children.length > 0) {
      const sum = node.children.reduce(
        (acc, c) => acc + (c.allocationPercent || 0),
        0
      );
      if (Math.abs(sum - 100) > 0.01) {
        errors.push(`"${node.name}" children: ${sum.toFixed(1)}% (need 100%)`);
      }
      node.children.forEach((child) => validateTree(child, errors));
    }

    if (node.factorValues && node.factorValues.length > 0) {
      const sum = node.factorValues.reduce(
        (acc, v) => acc + (v.allocationPercent || 0),
        0
      );
      if (Math.abs(sum - 100) > 0.01) {
        errors.push(`"${node.name}" values: ${sum.toFixed(1)}% (need 100%)`);
      }
    }

    return errors;
  };

  const handleUpdateNode = (nodeId, updates) => {
    const updated = updateNodeById(tree, nodeId, updates);
    onChange(updated);
    setValidationErrors(validateTree(updated));
  };

  const handleAddChild = (parentId, newChild) => {
    const updated = addChildById(tree, parentId, newChild);
    onChange(updated);
    setValidationErrors(validateTree(updated));
  };

  const handleDeleteNode = (nodeId) => {
    const updated = deleteNodeById(tree, nodeId);
    onChange(updated);
    setValidationErrors(validateTree(updated));
  };

  const handleSplitValueToChild = (parentId, valueIdx, newChild) => {
    const updated = splitValueToChild(tree, parentId, valueIdx, newChild);
    onChange(updated);
    setValidationErrors(validateTree(updated));
  };

  const handleValidate = () => {
    const errors = validateTree(tree);
    setValidationErrors(errors);
  };

  if (!tree) {
    return (
      <Box p={3} borderWidth="1px" borderRadius="md">
        <Text color="gray.500" fontSize="sm">
          No allocation tree initialized
        </Text>
        <Button
          mt={2}
          size="sm"
          onClick={() => {
            const initialTree = {
              id: crypto.randomUUID(),
              name: "Total Premium",
              level: 0,
              allocationPercent: 100,
              allocationAmount: premium,
              children: [],
            };
            onChange(initialTree);
          }}>
          Initialize Tree
        </Button>
      </Box>
    );
  }

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      p={2}
      backgroundColor="whiteAlpha.800">
      <HStack justify="space-between" mb={2}>
        <Text fontSize="md" fontWeight="bold">
          {title}
        </Text>
        <Button
          size="xs"
          onClick={handleValidate}
          colorScheme="blue"
          variant="outline">
          Validate
        </Button>
      </HStack>

      {validationErrors.length > 0 && (
        <Alert status="error" mb={2} borderRadius="md" p={2}>
          <AlertIcon boxSize={4} />
          <VStack align="start" spacing={0.5}>
            {validationErrors.map((err, i) => (
              <Text key={i} fontSize="xs">
                {err}
              </Text>
            ))}
          </VStack>
        </Alert>
      )}

      <AllocationTreeNode
        node={tree}
        level={0}
        onUpdateNode={handleUpdateNode}
        onAddChild={handleAddChild}
        onDeleteNode={handleDeleteNode}
        onSplitValueToChild={handleSplitValueToChild}
        premium={premium}
        parentAmount={0}
        onCopyFactors={onCopyFactors}
        enableFactorCopy={enableFactorCopy}
      />

      {/* Compact Tree Statistics */}
      <Box mt={2} p={2} bg="gray.50" borderRadius="md">
        <HStack spacing={3} fontSize="xs">
          <Text>Nodes: {countNodes(tree)}</Text>
          <Text>Leaves: {countLeafNodes(tree)}</Text>
          <Text>Depth: {getMaxDepth(tree)}</Text>
        </HStack>
      </Box>
    </Box>
  );
};

// Helper functions
const countNodes = (node) => {
  if (!node) return 0;
  let count = 1;
  if (node.children) {
    node.children.forEach((child) => {
      count += countNodes(child);
    });
  }
  return count;
};

const countLeafNodes = (node) => {
  if (!node) return 0;
  if (!node.children || node.children.length === 0) return 1;
  let count = 0;
  node.children.forEach((child) => {
    count += countLeafNodes(child);
  });
  return count;
};

const getMaxDepth = (node, depth = 0) => {
  if (!node) return depth;
  if (!node.children || node.children.length === 0) return depth;
  let maxChildDepth = depth;
  node.children.forEach((child) => {
    const childDepth = getMaxDepth(child, depth + 1);
    if (childDepth > maxChildDepth) maxChildDepth = childDepth;
  });
  return maxChildDepth;
};

export default AllocationTreeEditor;
